import { InjectionToken, NgZone, inject } from '@angular/core';

/**
 * Runs `fn` under an exclusive cross-tab Web Lock named `name`, released when `fn`'s promise settles.
 * Shared by the offline catalogue (two tabs never interleave a game's swap) and `WarehouseService`
 * (two tabs never have a change PUT on the wire together).
 */
export type CrossTabLock = <T>(name: string, fn: () => Promise<T>) => Promise<T>;

export const CROSS_TAB_LOCK = new InjectionToken<CrossTabLock>('CROSS_TAB_LOCK', {
  providedIn: 'root',
  factory: (): CrossTabLock => {
    const zone = inject(NgZone);
    return (name, fn) => {
      const locks = (typeof navigator !== 'undefined' ? (navigator as any)['locks'] : undefined) as
        | { request: (n: string, cb: () => Promise<unknown>) => Promise<unknown> }
        | undefined;
      if (locks && typeof locks.request === 'function') {
        let granted = false;
        // The lock callback is not zone-patched (same trap AuthRefreshService.withCrossTabLock
        // records): re-enter, so whatever it publishes repaints.
        return (locks.request(name, () => {
          granted = true;
          return zone.run(fn);
        }) as Promise<any>).catch(err => {
          // Rejected before granting -- a sandboxed origin, a document that is not fully active:
          // run unserialised rather than never, or a caller waiting on `fn` waits for the life of
          // the tab. A rejection from `fn` itself is the caller's, and `fn` never runs twice.
          if (granted) {
            throw err;
          }
          return zone.run(fn);
        });
      }
      // No Web Locks API (Safari < 15.4, a non-secure context): run unserialised.
      return fn();
    };
  },
});
